import { createSlice } from '@reduxjs/toolkit';
import { AppThunk } from './store';
import { apiString as API_URL } from '../utils/constants';
import axios from 'axios';
// import b64toBlob from 'b64-to-blob';
import { setIsLoading, setShowMessage } from './shared.slice';
import { BehaviorOrSafetyPlan } from '../Models/CarePlans/BehaviorOrSafetyPlan.model';

export const getDefaultPrimaryLanguage = () => {
    return { Id: '56695e83ad78ee0e48b04a01', Name: 'English' };
};

export const getDefaultCareCircle = () => {
    return {
        Id: null,
        Address: '',
        Email: '',
        HomePhone: '',
        IsDeleted: false,
        MobilePhone: '',
        Name: '',
        PermissionToReleaseHIPAAInformation: false,
        PreferredContact: -1,
        PrimaryLanguage: getDefaultPrimaryLanguage(),
        ReasonForDeleted: 0,
        Relation: '',
        SignedDocument: '',
        DischargeSupportPerson: false,
        NextOfKin: false,
    };
};

const initialState = {
    behavioralPlanInfo: new BehaviorOrSafetyPlan(),
    safetyPlanInfo: new BehaviorOrSafetyPlan(),
    modalTitle: '',
    modalTitleBehavioralPlan: '',
    modalState: {}, // { [sectionKey]: boolean }
    modalStateBehavioralPlan: {}, // { [sectionKey]: boolean }
    editId: {}, // { [sectionKey]: id|null }
    editIdBehavioralPlan: {}, // { [sectionKey]: id|null }
    fieldValues: {}, // { [sectionKey]: { ...fields } }
    fieldValuesBehavioralPlan: {}, // { [sectionKey]: { ...fields } }
    confirmOpen: false,
    confirmOpenBehavioralPlan: false,
    confirmItem: null,
    confirmItemBehavioralPlan: null,
    confirmType: '',
    confirmTypeBehavioralPlan: '',
    careCircleConfirmOpen: false,
    hasReviewed: false,
    pendingCareCircleItem: null,
    pendingCareCircleSection: null,
    showSafetyPlanModal: false,
};

const behaviorOrSafetyPlanSlice = createSlice({
    name: 'behaviorOrSafetyPlanSlice',
    initialState,
    reducers: {
        setBehavioralPlanInfo: (state, action) => {
            state.behavioralPlanInfo = action.payload;
        },
        setSafetyPlanInfo: (state, action) => {
            state.safetyPlanInfo = action.payload;
        },
        setModalTitleBehavioralPlan: (state, action) => {
            state.modalTitleBehavioralPlan = action.payload;
        },
        setModalTitle: (state, action) => {
            state.modalTitle = action.payload;
        },
        setModalStateBehavioralPlan: (state, action) => {
            state.modalStateBehavioralPlan = action.payload;
        },
        setModalState: (state, action) => {
            state.modalState = action.payload;
        },
        setEditIdBehavioralPlan: (state, action) => {
            state.editIdBehavioralPlan = action.payload;
        },
        setEditId: (state, action) => {
            state.editId = action.payload;
        },
        setFieldValuesBehavioralPlan: (state, action) => {
            state.fieldValuesBehavioralPlan = action.payload;
        },
        setFieldValues: (state, action) => {
            state.fieldValues = action.payload;
        },
        setConfirmOpenBehavioralPlan: (state, action) => {
            state.confirmOpenBehavioralPlan = action.payload;
        },
        setConfirmOpen: (state, action) => {
            state.confirmOpen = action.payload;
        },
        setConfirmItemBehavioralPlan: (state, action) => {
            state.confirmItemBehavioralPlan = action.payload;
        },
        setConfirmItem: (state, action) => {
            state.confirmItem = action.payload;
        },
        setConfirmTypeBehavioralPlan: (state, action) => {
            state.confirmTypeBehavioralPlan = action.payload;
        },
        setConfirmType: (state, action) => {
            state.confirmType = action.payload;
        },
        setCareCircleConfirmOpen: (state, action) => {
            state.careCircleConfirmOpen = action.payload;
        },
        setHasReviewed: (state, action) => {
            state.hasReviewed = action.payload;
        },
        setPendingCareCircleItem: (state, action) => {
            state.pendingCareCircleItem = action.payload;
        },
        setPendingCareCircleSection: (state, action) => {
            state.pendingCareCircleSection = action.payload;
        },
        setShowSafetyPlanModal: (state, action) => {
            state.showSafetyPlanModal = action.payload;
        },
    },
});

export const {
    setBehavioralPlanInfo,
    setSafetyPlanInfo,
    setModalTitle,
    setModalTitleBehavioralPlan,
    setModalState,
    setModalStateBehavioralPlan,
    setEditId,
    setEditIdBehavioralPlan,
    setFieldValues,
    setFieldValuesBehavioralPlan,
    setConfirmOpen,
    setConfirmOpenBehavioralPlan,
    setConfirmItem,
    setConfirmItemBehavioralPlan,
    setConfirmType,
    setConfirmTypeBehavioralPlan,
    setCareCircleConfirmOpen,
    setHasReviewed,
    setPendingCareCircleItem,
    setPendingCareCircleSection,
    setShowSafetyPlanModal,
} = behaviorOrSafetyPlanSlice.actions;

export const openSafetyPlanModal = (): AppThunk => (dispatch) => {
    dispatch(setShowSafetyPlanModal(true));
    dispatch(setHasReviewed(false));
};

export const saveSafetyPlan =
    (safetyPlan: BehaviorOrSafetyPlan): AppThunk =>
    async (dispatch) => {
        dispatch(setIsLoading(true));
        try {
            const isUpdate = Boolean(safetyPlan.Id);
            const url = isUpdate ? `${API_URL}/safetyplan/updatesafetyplan/` : `${API_URL}/safetyplan/insertsafetyplan/`;
            const response = await axios.put(url, safetyPlan);
            if (Boolean(response.data)) {
                dispatch(setSafetyPlanInfo(response.data));
            } else {
                throw new Error('The Safety Plan save returned no plan.');
            }
        } catch (error) {
            dispatch(setShowMessage(true, `Error saving safety plan: ${error.message}\nPlease try again.`, 'error'));
        } finally {
            dispatch(setIsLoading(false));
        }
    };

export const saveBehavioralPlan =
    (behavioralPlan: BehaviorOrSafetyPlan): AppThunk =>
    async (dispatch) => {
        dispatch(setIsLoading(true));
        dispatch(setBehavioralPlanInfo(behavioralPlan));
        try {
            const isUpdate = Boolean(behavioralPlan.Id);
            const url = isUpdate ? `${API_URL}/safetyplan/updatesafetyplan/` : `${API_URL}/safetyplan/insertsafetyplan/`;
            const response = await axios.put(url, behavioralPlan);
            if (Boolean(response.data)) {
                dispatch(setBehavioralPlanInfo(response.data));
            }
        } catch (error) {
            dispatch(setShowMessage(true, `Error saving behavioral plan: ${error.message}\nPlease try again.`, 'error'));
        } finally {
            dispatch(setIsLoading(false));
        }
    };

export const fetchSafetyPlanByMemberId =
    (memberId: string): AppThunk =>
    async (dispatch) => {
        dispatch(setIsLoading(true));
        try {
            const response = await axios.get(`${API_URL}/safetyplan/fetchsafetyplanbymemberid?memberId=${memberId}`);

            if (response.data) {
                dispatch(setSafetyPlanInfo(response.data));
            } else {
                dispatch(setSafetyPlanInfo(new BehaviorOrSafetyPlan()));
            }
        } catch (error) {
            dispatch(setShowMessage(true, `Error fetching safety plan: ${error.message}\nPlease try again.`, 'error'));
        } finally {
            dispatch(setIsLoading(false));
        }
    };

export const fetchBehavioralPlanByMemberId =
    (memberId: string): AppThunk =>
    async (dispatch) => {
        dispatch(setIsLoading(true));
        try {
            const response = await axios.get(`${API_URL}/safetyplan/fetchbehavioralplanbymemberid?memberId=${memberId}`);
            if (response.data) {
                dispatch(setBehavioralPlanInfo(response.data));
            } else {
                dispatch(setBehavioralPlanInfo(new BehaviorOrSafetyPlan()));
            }
        } catch (error) {
            dispatch(setShowMessage(true, `Error fetching behavioral plan: ${error.message}\nPlease try again.`, 'error'));
        } finally {
            dispatch(setIsLoading(false));
        }
    };

export const saveCareCircleItem =
    (careCircleItem: any, memberId: string): AppThunk =>
    async (dispatch) => {
        dispatch(setIsLoading(true));
        try {
            const response = await axios.put(`${API_URL}/member/addcaregiver?memberId=${memberId}`, careCircleItem);
            if (response.data) {
                dispatch(setShowMessage(true, 'New Care Circle entry saved successfully.', 'success'));
            }
        } catch (error) {
            console.error('Error saving care circle item:', error);
        } finally {
            dispatch(setIsLoading(false));
        }
    };

export const savePlanPDFToDocuments =
    (planId: string): AppThunk =>
    async (dispatch) => {
        dispatch(setIsLoading(true));
        try {
            const response = await axios.put(`${API_URL}/safetyplan/savesafetyplandocument?planId=${planId}`);
            console.log('Response from saving plan PDF:', response.data);
        } catch (error) {
            dispatch(setShowMessage(true, `Error fetching behavioral plan: ${error.message}\nPlease try again.`, 'error'));
        } finally {
            dispatch(setIsLoading(false));
        }
    };

export default behaviorOrSafetyPlanSlice.reducer;
